import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";
import { logoutUser } from "@/services/auth.service";
import { ROUTES } from "@/config/constants";
import Button from "./ui/Button";
import logo from "@/assets/icons/Icon_1.png";
import darkLogo from "@/assets/icons/Icon_4.png";
import { useUIStore } from "@/store/uiStore";
import { FiSun, FiMoon, FiMenu, FiX } from "react-icons/fi";

const Navbar: React.FC = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const theme = useUIStore((state) => state.theme);
  const toggleTheme = useUIStore((state) => state.toggleTheme);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await logoutUser();
      navigate(ROUTES.HOME);
      setMobileMenuOpen(false);
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const handleBookSession = () => {
    closeMobileMenu();
    if (user) {
      navigate(ROUTES.BOOKING);
    } else {
      navigate(ROUTES.LOGIN, {
        state: {
          returnTo: ROUTES.BOOKING,
          message: "Please sign in or create an account to book a session.",
        },
      });
    }
  };

  return (
    <nav className="bg-white/95 dark:bg-gray-900/95 backdrop-blur border-b border-gray-200 dark:border-gray-800 shadow-md sticky top-0 z-40 transition-colors">
      <div className="section-container">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <Link to={ROUTES.HOME} className="flex items-center space-x-2">
            <img
              src={theme === "dark" ? darkLogo : logo}
              alt="WIH Media"
              width="96"
              height="96"
              className="h-16 md:h-20 lg:h-24 w-auto drop-shadow-md"
            />
          </Link>

          {/* Desktop Navigation Links */}
          <div className="hidden md:flex items-center space-x-8">
            <Link
              to={ROUTES.HOME}
              className="text-gray-700 dark:text-gray-100 hover:text-primary-500 dark:hover:text-primary-400 transition-colors"
            >
              Home
            </Link>
            <Link
              to={ROUTES.SERVICES}
              className="text-gray-700 dark:text-gray-100 hover:text-primary-500 dark:hover:text-primary-400 transition-colors"
            >
              Services
            </Link>
            <Link
              to={ROUTES.PORTFOLIO}
              className="text-gray-700 dark:text-gray-100 hover:text-primary-500 dark:hover:text-primary-400 transition-colors"
            >
              Portfolio
            </Link>
            <Link
              to={ROUTES.PROPERTIES_MAP}
              className="text-gray-700 dark:text-gray-100 hover:text-primary-500 dark:hover:text-primary-400 transition-colors"
            >
              Properties Map
            </Link>
            <Link
              to={ROUTES.SHOWROOM}
              className="text-gray-700 dark:text-gray-100 hover:text-primary-500 dark:hover:text-primary-400 transition-colors"
            >
              Showroom
            </Link>
            <Link
              to={ROUTES.FAQ}
              className="text-gray-700 dark:text-gray-100 hover:text-primary-500 dark:hover:text-primary-400 transition-colors"
            >
              FAQ
            </Link>
            <Link
              to={ROUTES.CONTACT}
              className="text-gray-700 dark:text-gray-100 hover:text-primary-500 dark:hover:text-primary-400 transition-colors"
            >
              Contact
            </Link>
            <Button variant="primary" size="sm" onClick={handleBookSession}>
              Book a Session
            </Button>
          </div>

          {/* Desktop Theme toggle + Auth Buttons */}
          <div className="hidden md:flex items-center space-x-3">
            <button
              type="button"
              onClick={toggleTheme}
              className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              aria-label="Toggle theme"
            >
              {theme === "dark" ? <FiSun className="w-5 h-5" /> : <FiMoon className="w-5 h-5" />}
            </button>
            {user ? (
              <>
                <Link to={ROUTES.CUSTOMER_DASHBOARD}>
                  <Button variant="outline" size="sm">
                    Dashboard
                  </Button>
                </Link>
                {user.role === "admin" && (
                  <Link to={ROUTES.ADMIN_DASHBOARD}>
                    <Button variant="ghost" size="sm">
                      Admin
                    </Button>
                  </Link>
                )}
                <Button variant="secondary" size="sm" onClick={handleLogout}>
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Link to={ROUTES.LOGIN}>
                  <Button variant="ghost" size="sm">
                    Login
                  </Button>
                </Link>
                <Link to={ROUTES.SIGNUP}>
                  <Button variant="primary" size="sm">
                    Sign Up
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center space-x-2">
            <button
              type="button"
              onClick={toggleTheme}
              className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="Toggle theme"
            >
              {theme === "dark" ? <FiSun className="w-5 h-5" /> : <FiMoon className="w-5 h-5" />}
            </button>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="Toggle mobile menu"
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <FiX className="w-6 h-6" /> : <FiMenu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden pb-4 border-t border-gray-200 dark:border-gray-700 mt-2">
            <div className="flex flex-col space-y-2 pt-4">
              <Link
                to={ROUTES.HOME}
                onClick={closeMobileMenu}
                className="px-4 py-3 text-gray-700 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-primary-500 dark:hover:text-primary-400 rounded-lg transition-colors min-h-[44px] flex items-center"
              >
                Home
              </Link>
              <Link
                to={ROUTES.SERVICES}
                onClick={closeMobileMenu}
                className="px-4 py-3 text-gray-700 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-primary-500 dark:hover:text-primary-400 rounded-lg transition-colors min-h-[44px] flex items-center"
              >
                Services
              </Link>
              <Link
                to={ROUTES.PORTFOLIO}
                onClick={closeMobileMenu}
                className="px-4 py-3 text-gray-700 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-primary-500 dark:hover:text-primary-400 rounded-lg transition-colors min-h-[44px] flex items-center"
              >
                Portfolio
              </Link>
              <Link
                to={ROUTES.PROPERTIES_MAP}
                onClick={closeMobileMenu}
                className="px-4 py-3 text-gray-700 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-primary-500 dark:hover:text-primary-400 rounded-lg transition-colors min-h-[44px] flex items-center"
              >
                Properties Map
              </Link>
              <Link
                to={ROUTES.SHOWROOM}
                onClick={closeMobileMenu}
                className="px-4 py-3 text-gray-700 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-primary-500 dark:hover:text-primary-400 rounded-lg transition-colors min-h-[44px] flex items-center"
              >
                Showroom
              </Link>
              <Link
                to={ROUTES.FAQ}
                onClick={closeMobileMenu}
                className="px-4 py-3 text-gray-700 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-primary-500 dark:hover:text-primary-400 rounded-lg transition-colors min-h-[44px] flex items-center"
              >
                FAQ
              </Link>
              <Link
                to={ROUTES.CONTACT}
                onClick={closeMobileMenu}
                className="px-4 py-3 text-gray-700 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-primary-500 dark:hover:text-primary-400 rounded-lg transition-colors min-h-[44px] flex items-center"
              >
                Contact
              </Link>
              <div className="px-4 pt-2">
                <Button variant="primary" size="md" fullWidth onClick={handleBookSession}>
                  Book a Session
                </Button>
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-2 space-y-2">
                {user ? (
                  <>
                    <Link
                      to={ROUTES.CUSTOMER_DASHBOARD}
                      onClick={closeMobileMenu}
                      className="block"
                    >
                      <Button variant="outline" size="md" fullWidth>
                        Dashboard
                      </Button>
                    </Link>
                    {user.role === "admin" && (
                      <Link to={ROUTES.ADMIN_DASHBOARD} onClick={closeMobileMenu} className="block">
                        <Button variant="ghost" size="md" fullWidth>
                          Admin
                        </Button>
                      </Link>
                    )}
                    <Button variant="secondary" size="md" fullWidth onClick={handleLogout}>
                      Logout
                    </Button>
                  </>
                ) : (
                  <>
                    <Link to={ROUTES.LOGIN} onClick={closeMobileMenu} className="block">
                      <Button variant="ghost" size="md" fullWidth>
                        Login
                      </Button>
                    </Link>
                    <Link to={ROUTES.SIGNUP} onClick={closeMobileMenu} className="block">
                      <Button variant="primary" size="md" fullWidth>
                        Sign Up
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
